/**
 * Pure game logic for Poker Dice, ported from Python backend.
 * Handles scoring, combination detection, and total calculation locally.
 */

export const SCHOOL_FACES: Record<string, number> = {
  ones: 1,
  twos: 2,
  threes: 3,
  fours: 4,
  fives: 5,
  sixes: 6,
};

export const COMBO_KEYS = [
  "one_pair",
  "two_pairs",
  "three_of_kind",
  "straight1",
  "straight2",
  "full_house",
  "four_of_kind",
  "poker",
  "garbage",
];

export type SchoolCategory = keyof typeof SCHOOL_FACES;
export type ComboCategory = typeof COMBO_KEYS[number];
export type AllCategory = SchoolCategory | ComboCategory;

export interface ScorecardValues {
  [key: string]: number | null;
}

export function calculateSchoolBalance(face: number, diceCount: number): number {
  return (diceCount - 3) * face;
}

export function computeSchoolTotals(schoolValues: ScorecardValues) {
  const filled = Object.entries(schoolValues).filter(([k, v]) => k in SCHOOL_FACES && v !== null);
  const complete = filled.length === 6;
  const balanceSum = filled.reduce((sum, [_, v]) => sum + (v as number), 0);
  let bonus = 0;

  if (complete) {
    const values = filled.map(([_, v]) => v as number);
    const allPositive = values.every((v) => v > 0);
    const allZero = values.every((v) => v === 0);

    if (balanceSum < 0) {
      bonus = -50;
    } else if (allZero) {
      bonus = 50;
    } else if (allPositive) {
      bonus = 100;
    }
  }

  return {
    sum: balanceSum,
    bonus,
    total: complete ? balanceSum + bonus : balanceSum,
    complete,
  };
}

export function computeComboTotals(comboValues: ScorecardValues) {
  const filled = Object.entries(comboValues).filter(([k, v]) => COMBO_KEYS.includes(k) && v !== null);
  const complete = filled.length === COMBO_KEYS.length;
  const comboSum = filled.reduce((sum, [_, v]) => sum + (v as number), 0);
  
  let completionBonus = 0;
  if (complete) {
    // Check if any combo (except garbage) is crossed out (0)
    const hasCrossedOut = filled.some(([k, v]) => k !== "garbage" && v === 0);
    if (!hasCrossedOut) {
      completionBonus = 100;
    }
  }

  return {
    sum: comboSum,
    completionBonus,
    total: comboSum + completionBonus,
    complete,
  };
}

export function computeScorecardTotal(
  schoolValues: ScorecardValues,
  comboValues: ScorecardValues
) {
  const school = computeSchoolTotals(schoolValues);
  const combos = computeComboTotals(comboValues);
  return {
    school_sum: school.sum,
    school_bonus: school.bonus,
    school_total: school.total,
    school_complete: school.complete,
    combo_sum: combos.sum,
    combo_completion_bonus: combos.completionBonus,
    combo_total: combos.total,
    combo_complete: combos.complete,
    grand_total: school.total + combos.total,
  };
}

/**
 * Suggests scores based on current dice.
 * hasComboBonus: if it's the first roll of the turn, lower section scores are doubled.
 */
export function suggestScores(dice: number[], hasComboBonus: boolean = false): Record<string, number> {
  const counts: Record<number, number> = {};
  dice.forEach((d) => (counts[d] = (counts[d] || 0) + 1));

  const suggestions: Record<string, number> = {};

  // ── School ──
  Object.entries(SCHOOL_FACES).forEach(([cat, face]) => {
    suggestions[cat] = calculateSchoolBalance(face, counts[face] || 0);
  });

  // ── Combinations ──
  const multiplier = hasComboBonus ? 2 : 1;
  const faces = Object.keys(counts).map(Number);
  const values = Object.values(counts).sort((a, b) => b - a);
  
  // 1 pair
  const pairFaces = faces.filter((f) => counts[f] >= 2);
  suggestions["one_pair"] = pairFaces.length > 0 ? Math.max(...pairFaces) * 2 * multiplier : 0;

  // 2 pairs
  if (pairFaces.length >= 2) {
    suggestions["two_pairs"] = pairFaces.sort((a, b) => b - a).slice(0, 2).reduce((s, f) => s + f * 2, 0) * multiplier;
  } else if (values.some((v) => v >= 4)) {
    const quadFace = faces.find((f) => counts[f] >= 4)!;
    suggestions["two_pairs"] = quadFace * 4 * multiplier;
  } else {
    suggestions["two_pairs"] = 0;
  }

  // 3 of kind
  const threeFaces = faces.filter((f) => counts[f] >= 3);
  suggestions["three_of_kind"] = threeFaces.length > 0 ? Math.max(...threeFaces) * 3 * multiplier : 0;

  // Straights
  const diceSet = new Set(dice);
  const s1 = [1, 2, 3, 4, 5].every((n) => diceSet.has(n));
  const s2 = [2, 3, 4, 5, 6].every((n) => diceSet.has(n));
  suggestions["straight1"] = s1 ? 15 * multiplier : 0;
  suggestions["straight2"] = s2 ? 20 * multiplier : 0;

  // Full House (flat +10 is not doubled)
  const isFullHouse = (values[0] === 3 && values[1] === 2) || values[0] === 5;
  const diceSum = dice.reduce((a, b) => a + b, 0);
  suggestions["full_house"] = isFullHouse ? diceSum * multiplier + 10 : 0;

  // 4 of kind (flat +20 is not doubled)
  const fourFaces = faces.filter((f) => counts[f] >= 4);
  suggestions["four_of_kind"] = fourFaces.length > 0 ? Math.max(...fourFaces) * 4 * multiplier + 20 : 0;

  // Poker (flat +50 is not doubled)
  const isPoker = values[0] === 5;
  suggestions["poker"] = isPoker ? diceSum * multiplier + 50 : 0;

  // Garbage
  suggestions["garbage"] = diceSum * multiplier;

  return suggestions;
}
